import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import type { ModuleTopicUids } from '@/chat/language-switch';
import type { ChatState } from '@/chat/reducer';
import type { ScriptMeta } from '@/chat/types';
import type { Audience, TopicDocument } from '@/cms/topics';
import type { Buddy } from '@/store/user';
import type { AppTheme } from '@/utils/theme';
import { ModuleTopicUidsContext } from '@/chat/language-switch';
import { checkpointStates, moduleProgress } from '@/chat/reducer';
import { slicesToScript } from '@/chat/script';
import { useChatRun } from '@/chat/store';
import { AppHeader } from '@/components/header';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerProvider,
  MessageScrollerViewport,
  useMessageScroller,
} from '@/components/ui/message-scroller';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { useStrings } from '@/i18n/use-strings';
import { track } from '@/lib/analytics';
import { useUserStore } from '@/store/user';
import { AnswerControl } from './answer-control';
import { ChatGround } from './chat-ground';
import { ChatThread } from './messages';
import { ModuleProgress } from './module-header';

/**
 * The learn screen. One component for the real topic route and the dev fixture
 * route, so what a fixture proves is what a learner gets.
 *
 * The height chain matters: `h-dvh` here, `min-h-0 flex-1` on the scroller.
 * `MessageScroller` fills its parent, so without it the viewport collapses and
 * auto-scroll looks broken.
 */

/**
 * The activity card to hold on screen instead of the newest message: one
 * re-armed after a miss, or one whose answer was just revealed.
 */
function cardToShow(state: ChatState): string | null {
  if (state.pending?.kind === 'activity' && state.attempt > 1) {
    return state.pending.stepId;
  }

  const last = state.thread.at(-1);

  return last?.kind === 'feedback' && last.reveal === true ? last.id.replace(/-fb-reveal$/, '') : null;
}

/**
 * Keeps the newest message, or `cardId` when set, on screen when the *footer*
 * changes size.
 *
 * `autoScroll` follows new content, but swapping one answer control for a
 * taller one shrinks the viewport instead of adding to it — the thread does
 * not move, the window around it does, and the last bubble ends up under the
 * fold. Re-pinning on every prompt change costs nothing and is the difference
 * between a chat that holds its place and one that quietly loses it.
 */
function PinToLatest({ signal, cardId }: { signal: string; cardId: string | null }) {
  const { scrollToEnd, scrollToMessage } = useMessageScroller();

  useEffect(() => {
    // Through the scroller rather than `scrollIntoView`, so it also stops
    // following the bottom while the footer and card change height.
    if (cardId !== null && scrollToMessage(cardId, { align: 'start', behavior: 'auto', scrollMargin: 12 })) {
      return;
    }

    scrollToEnd({ behavior: 'auto' });
  }, [signal, cardId, scrollToEnd, scrollToMessage]);

  return null;
}

/**
 * The footer when the chat is not waiting on an answer. It holds the way on
 * once the module is complete, and a status line of the same height before
 * then, so the bar never changes height under a thumb.
 */
function ModuleExit({ complete, course, onFinish }: { complete: boolean; course: boolean; onFinish: () => void }) {
  const { chat, completion } = useStrings();

  if (complete) {
    return (
      <Button
        size="xl"
        variant="destructive"
        // The Autopilot's one signal that the module is over. It lives on
        // exactly one element, which is why the completion card no longer
        // carries a Continue of its own.
        data-autopilot="completion-continue"
        onClick={onFinish}
      >
        {course ? completion.completion_get_passport : completion.completion_continue}
        <Icon name="passport/arrow-right" aria-hidden className="size-4 rtl:rotate-180" />
      </Button>
    );
  }

  // Nothing tappable while the coach talks. Answers land here a beat later,
  // so a button would catch taps meant for them.
  return (
    <p aria-live="off" className="text-chat-ink-muted flex min-h-13 items-center justify-center text-sm font-semibold">
      {chat.chat_coach_typing}
    </p>
  );
}

export type ChatModuleProps = {
  topic: TopicDocument;
  meta: ScriptMeta;
  theme: AppTheme;
  buddy: Buddy;
  language: string;
  audience: Audience;
  /** True when finishing this module finishes the whole course. */
  completesCourse: boolean;
  /**
   * This module's topic UID in each language, for the header's language
   * switch. Omitted by the fixture route, which has no other language to go to.
   */
  topicUids?: ModuleTopicUids;
};

/**
 * Tracks the answer control's height.
 *
 * The footer is taken out of flow and overlays the scroller, and the thread
 * reserves exactly that much space below its last message. A footer that
 * changes size — quick replies swapped for four stacked answers — then adds
 * room under the conversation instead of squeezing the viewport it sits in,
 * so nothing already on screen moves. Measured, not guessed, because the
 * control's height depends on the copy an editor wrote.
 */
function useFooterHeight() {
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState(104);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        setHeight(entry.borderBoxSize[0]?.blockSize ?? entry.target.getBoundingClientRect().height);
      }
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, []);

  return { ref, height };
}

export function ChatModule({
  topic,
  meta,
  theme,
  buddy,
  language,
  audience,
  completesCourse,
  topicUids,
}: ChatModuleProps) {
  const { chat } = useStrings();
  const footer = useFooterHeight();
  const navigate = useNavigate();
  // The gating video's player. It is opened from the video's own card *and*
  // from the answer footer, so the flag cannot live inside the card.
  const [gateOpen, setGateOpen] = useState(false);
  const script = useMemo(() => slicesToScript(topic.data.slices, meta), [topic, meta]);
  // Optional, and blank is the normal case: with no asset the canvas keeps its
  // built-in per-theme backdrop. See `ChatGround`.
  const { siteSettings } = useRootLoaderData();
  const chatBackground = siteSettings.chatBackgroundUrl;
  // The document id, not the module key, is the run's identity: one module has
  // a topic per language, and switching language has to rebuild the run
  // against the new script rather than leave the old thread on screen. The
  // saved inputs replay into it, so the learner keeps their place.
  const { chat: state, dispatch, typing } = useChatRun({ script, language, audience, runKey: topic.id });

  const checkpoints = checkpointStates(script, state);
  const progress = moduleProgress(script, state);
  // `duration_minutes` is new in this model, so a carried-over topic has it
  // unset — and an unset Prismic Number reads as `undefined`, not `null`.
  const duration = topic.data.duration_minutes;
  // Scaled by steps played, not by checkpoints reached: on a module with one
  // checkpoint the old measure never moved off the full duration until the
  // module ended.
  const minutesLeft =
    typeof duration === 'number' && duration > 0 ? Math.max(1, Math.round(duration * (1 - progress.fraction))) : null;

  const journeyHref = `/h/${theme}/learn/topics`;
  const finishHref = completesCourse ? `/h/${theme}/passport` : journeyHref;

  /** Leaves a finished module. Called only by the footer's button. */
  const finishModule = () => {
    if (completesCourse) {
      useUserStore.getState().setProfile({ completed: true });
      track('completion', { language, audience });
    }

    void navigate(finishHref);
  };

  return (
    <ModuleTopicUidsContext.Provider value={topicUids ?? null}>
      <div
        className="chat-canvas relative flex h-dvh flex-col"
        data-chat-theme={theme}
        data-chat-backdrop={chatBackground === null ? undefined : 'custom'}
      >
        {chatBackground === null ? null : <ChatGround src={chatBackground} />}
        {/*
        The same header the journey renders, with the module's checkpoint strip
        in its progress slot instead of the course bar. `backHref` is the one
        thing a module wants that a destination screen does not.
      */}
        <AppHeader
          variant="full"
          backHref={journeyHref}
          className="shrink-0"
          progress={
            <ModuleProgress
              moduleIndex={meta.moduleIndex}
              moduleTotal={meta.moduleTotal}
              title={meta.title}
              checkpoints={checkpoints}
              fraction={progress.fraction}
              minutesLeft={minutesLeft}
            />
          }
        />

        {/*
        The thread is the page's main content, so it is the landmark that
        carries the header's skip-link target — without an `#main-content` the
        skip link the header renders points at nothing. The height chain runs
        through it unchanged: `min-h-0 flex-1` on both the landmark and the
        scroller inside it.
      */}
        <main id="main-content" className="flex min-h-0 flex-1 flex-col">
          <MessageScrollerProvider autoScroll defaultScrollPosition="end">
            <MessageScroller className="min-h-0 flex-1">
              <MessageScrollerViewport className="scroll-fade-y chat-scroll-smooth overscroll-none-safe">
                <MessageScrollerContent
                  // The thread is the chat's one live region: `role="log"` comes
                  // from the primitive and its politeness is only implicit, so
                  // it is stated here. Every landed message — the coach, the
                  // learner's echo and, per ticket 10, feedback — is announced
                  // through this and nothing nests a second region inside it.
                  aria-live="polite"
                  className="mx-auto w-full max-w-2xl gap-4 px-4 pt-5"
                  // The scroller's own spacer bottom-anchors a short thread by
                  // pushing it down, which means every early message moves the
                  // ones above it. A lesson reads top-down; drop the spacer.
                  spacerClassName="hidden"
                  style={{ paddingBottom: footer.height + 20 }}
                >
                  <ChatThread
                    messages={state.thread}
                    typing={typing}
                    context={{
                      buddy,
                      gateStepId: state.pending?.kind === 'gate' ? state.pending.stepId : null,
                      gateOpen,
                      onGateOpenChange: setGateOpen,
                      onGateContinue: () => {
                        setGateOpen(false);
                        dispatch({ type: 'answer', payload: { kind: 'gate' } });
                      },
                      courseComplete: completesCourse,
                    }}
                  />
                </MessageScrollerContent>
              </MessageScrollerViewport>
              <PinToLatest
                signal={`${state.pending?.stepId ?? ''}:${state.attempt}:${state.thread.length}`}
                cardId={cardToShow(state)}
              />
              {/*
            Parked in the corner rather than the shadcn default of dead centre,
            where it sits on top of the message it is offering to scroll to.
          */}
              <MessageScrollerButton
                aria-label={chat.chat_scroll_latest}
                className="right-3 left-auto translate-x-0 rtl:right-auto rtl:left-3 rtl:translate-x-0"
              />
            </MessageScroller>
          </MessageScrollerProvider>
        </main>

        {/*
        Out of flow, over the thread: see `useFooterHeight`. Height is never
        transitioned — animating a layout property here would move the whole
        conversation for the duration of the animation.
      */}
        <footer
          ref={footer.ref}
          className="border-chat-hairline bg-bubble-buddy/92 absolute inset-x-0 bottom-0 border-t backdrop-blur-sm"
          // The home indicator's inset, added to the bar rather than to the page:
          // `useFooterHeight` measures this element, so the thread's bottom
          // padding follows it and the last message still clears the controls.
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="mx-auto flex min-h-[76px] w-full max-w-2xl items-center px-4 py-3">
            <div className="w-full">
              {state.pending ? (
                <AnswerControl
                  prompt={state.pending}
                  attempt={state.attempt}
                  onAnswer={(payload) => dispatch({ type: 'answer', payload })}
                  onOpenGate={() => setGateOpen(true)}
                  onReveal={() => dispatch({ type: 'reveal' })}
                />
              ) : (
                <ModuleExit complete={state.status === 'complete'} course={completesCourse} onFinish={finishModule} />
              )}
            </div>
          </div>
        </footer>
      </div>
    </ModuleTopicUidsContext.Provider>
  );
}
